var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import * as React from "react";
import { Label } from "./label";
import { Icon } from "./icon";
import { FlexContainer } from "../grid/flexcontainer";
import { bind } from "decko";
import { componentTranslations } from "./i18n";
import { css, cx } from "emotion";
import { getThemeColorVar } from "../helper/color";
const inputClass = css({
    width: "100%",
    "input,select,textarea": {
        backgroundColor: getThemeColorVar("background", "default"),
        border: "none",
        color: getThemeColorVar("typo", "default"),
        display: "block",
        fontWeight: 400,
        lineHeight: "20px",
        padding: "9px",
        width: "100%",
        minWidth: "0px",
        borderRadius: "4px",
        "&:focus": {
            backgroundColor: getThemeColorVar("background", "default"),
            borderColor: getThemeColorVar("background", "lighter"),
            outline: "none",
        },
        "&::placeholder": {
            color: getThemeColorVar("typo", "lighter"),
            fontStyle: "italic",
        },
        "&:disabled": {
            cursor: "not-allowed",
            color: getThemeColorVar("typo", "default"),
        },
        "&:invalid": {
            boxShadow: "none",
        },
    },
    textarea: {
        minWidth: "100%",
        maxWidth: "100%",
        resize: "vertical",
    },
    label: {
        color: getThemeColorVar("typo", "default"),
        display: "block",
        fontSize: "14px",
        fontWeight: 600,
        lineHeight: "25px",
        marginBottom: "5px",
    },
});
const inputSmallClass = css({
    "input,select,textarea,p": {
        fontSize: "14px",
        fontWeight: "normal",
        lineHeight: "20px",
        padding: "5px 0",
    },
    input: {
        padding: "7px",
    },
});
const inputWrapperClass = css({
    border: `1px solid ${getThemeColorVar("border", "default")}`,
    backgroundColor: getThemeColorVar("background", "lighter"),
    borderRadius: "4px",
    "&:focus-within": {
        border: `1px solid ${getThemeColorVar("border", "dark")}`,
    },
});
const inputWrapperIsInvalidClass = css({
    border: `1px solid ${getThemeColorVar("negative", "default")}`,
    color: getThemeColorVar("typo", "default"),
    borderRadius: "4px",
});
const disabledClass = css({
    "label:not(:focus)": {
        color: getThemeColorVar("typo", "light"),
    },
    "input[disabled]:not(:focus), select[disabled]:not(:focus)": {
        color: getThemeColorVar("typo", "default"),
        backgroundColor: getThemeColorVar("background", "lighter"),
        "&::placeholder": {
            color: getThemeColorVar("typo", "light"),
        },
    },
});
const inputWrapperIsDisabledClass = css({
    border: `1px solid ${getThemeColorVar("border", "default")}`,
});
const requiredMarkerClass = css({
    color: getThemeColorVar("negative", "default"),
    fontWeight: 600,
});
const validationErrorClass = css({
    margin: "5px 0 0 0",
    padding: 0,
    "> li": {
        listStyle: "none",
        color: getThemeColorVar("negative", "default"),
    },
});
export class GenericInputElement extends React.Component {
    constructor(props) {
        super(props);
        this.defaultState = {
            isTouched: false,
            isPristine: true,
        };
        this.translate = componentTranslations();
        this.validators = [];
    }
    validate(value) {
        const errors = [];
        const maybeCustomValidators = this.props.customValidation;
        const customValidators = maybeCustomValidators !== null && maybeCustomValidators !== void 0 ? maybeCustomValidators : [];
        let activeValidators = this.validators.concat(customValidators);
        if (value === "") {
            activeValidators = [];
        }
        if (this.props.required) {
            activeValidators.push((value) => {
                return {
                    valid: value != "",
                    validationMessage: this.translate.validation.required,
                };
            });
        }
        for (let validator of activeValidators) {
            const r = validator(value);
            if (!r.valid) {
                errors.push(r);
            }
        }
        if (this.inputElRef) {
            const ref = this.inputElRef;
            if (ref.setCustomValidity) {
                if (errors.length > 0) {
                    ref.setCustomValidity(errors[0].validationMessage);
                }
                else {
                    ref.setCustomValidity("");
                }
            }
        }
        return errors;
    }
    defaultHandleFocus(event) {
        if (document.activeElement !== this.inputElRef) {
            this.setState({
                isTouched: true,
            });
        }
        this.props.onFocus && this.props.onFocus(event);
    }
    defaultHandleBlur(event) {
        if (document.activeElement !== this.inputElRef) {
            this.setState({
                isTouched: true,
            });
        }
        this.props.onBlur && this.props.onBlur(event);
    }
    renderErrors(validationErrors) {
        return (validationErrors.length > 0 && (React.createElement("ul", { className: cx(validationErrorClass) }, validationErrors.map((error, idx) => (React.createElement("li", { key: idx },
            React.createElement(FlexContainer, { spaceBetween: "base" },
                React.createElement(Icon, { name: "cancel" }),
                React.createElement("span", null, error.validationMessage))))))));
    }
    render() {
        const divClasses = [inputClass];
        const inputWrapperClasses = [inputWrapperClass];
        const validationErrors = this.validate(this.props.value);
        this.props.size === "small" && divClasses.push(inputSmallClass);
        validationErrors.length > 0 &&
            !this.state.isPristine &&
            inputWrapperClasses.push(inputWrapperIsInvalidClass);
        if (this.props.disabled) {
            divClasses.push(disabledClass);
            inputWrapperClasses.push(inputWrapperIsDisabledClass);
        }
        return (React.createElement("div", { className: cx(divClasses) },
            this.props.label && (React.createElement(FlexContainer, { spaceBetween: "sm" },
                React.createElement(Label, { text: this.props.label || "" }),
                this.props.required && (React.createElement("span", { className: cx(requiredMarkerClass) }, " *")))),
            React.createElement("div", { className: cx(inputWrapperClasses) }, this.renderInner()),
            !this.state.isPristine && this.renderErrors(validationErrors)));
    }
}
__decorate([
    bind,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", void 0)
], GenericInputElement.prototype, "defaultHandleFocus", null);
__decorate([
    bind,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", void 0)
], GenericInputElement.prototype, "defaultHandleBlur", null);
export default GenericInputElement;
//# sourceMappingURL=genericinputfield.js.map